import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  DollarSign,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";

export default function ManageProduce() {
  const params = useParams();
  const navigate = useNavigate();

  const id = params.itemid;
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [produce, setProduce] = useState(null);
  const [showStageAddForm, setShowStageAddForm] = useState(false);
  const [expenseForms, setExpenseForms] = useState([
    {
      id: 1,
      type: "",
      amount: "",
      notes: "",
    },
  ]);
  const [expandedStages, setExpandedStages] = useState({});
  const [newStage, setNewStage] = useState({
    name: "",
    date: "",
    endDate: "",
    cost: "",
  });
  const [newCare, setNewCare] = useState({
    action: "",
    date: "",
    cost: "",
    notes: "",
  });
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [isAddingStage, setIsAddingStage] = useState(false);
  const [isAddingCare, setIsAddingCare] = useState(false);
  const [isDeletingCare, setIsDeletingCare] = useState(false);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  useEffect(() => {
    const fetchFarmData = async () => {
      try {
        if (!produce) {
          setLoading(true);
        }

        // Try to get from cache first for immediate display
        const cachedResult = await cacheService.getCachedResponse(`/manage/farm/${id}`, 'GET');
        if (cachedResult && cachedResult.status && cachedResult.data && !produce) {
          const data = cachedResult.data;
          const formattedProduce = formatProduceData(data);
          setProduce(formattedProduce);
          setLoading(false);
        }

        const result = await authAPI.getSingleFarmManagement(id);

        if (result.status) {
          const data = result.data;
          const formattedProduce = formatProduceData(data);

          setProduce(formattedProduce);
        } else {
          if (!produce) {
            showToast('error', result.message || 'Failed to load farm data');
          }
        }
      } catch (error) {
        console.error('Error fetching farm data:', error);
        if (!produce) {
          showToast('error', 'Failed to load farm data');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFarmData();
    } else {
      setLoading(false);
    }

    // Listen for background sync completion to refresh data
    const handleSyncComplete = () => {
      refreshFarmData();
    };

    window.addEventListener('sync-complete', handleSyncComplete);
    window.addEventListener('online', handleSyncComplete);

    return () => {
      window.removeEventListener('sync-complete', handleSyncComplete);
      window.removeEventListener('online', handleSyncComplete);
    };
  }, [id]);

  const formatProduceData = (data) => {
    return {
      id: data.id,
      name: data.name,
      type: "crop",
      image: data.image,
      startDate: data.growing_stages?.length > 0
        ? new Date(Math.min(...data.growing_stages.map(stage => new Date(stage.start_date)))).toISOString().split('T')[0]
        : "Not planted yet",
      expectedHarvest: data.growing_stages?.length > 0
        ? new Date(Math.max(...data.growing_stages.map(stage => new Date(stage.end_date)))).toISOString().split('T')[0]
        : "Not harvested yet",
      totalExpenses: parseFloat(data.total_expenses) || 0,
      careExpenses: parseFloat(data.care_cost) || 0,
      currentStage: data.growing_stages?.find(stage => stage.is_current)?.stage_name || "Not started",
      nextStage: (() => {
        if (!data.growing_stages || data.growing_stages.length === 0) return "None";

        const sortedStages = [...data.growing_stages].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        const currentStageIndex = sortedStages.findIndex(stage => stage.is_current);
        if (currentStageIndex === -1 || currentStageIndex === sortedStages.length - 1) {
          return "None";
        }

        return sortedStages[currentStageIndex + 1].stage_name;
      })(),
      progress: 0,
      stages: data.growing_stages?.map(stage => ({
        id: stage.id,
        name: stage.stage_name,
        date: new Date(stage.start_date).toISOString().split('T')[0],
        endDate: new Date(stage.end_date).toISOString().split('T')[0],
        cost: stage.stage_cost || 0,
        current: stage.is_current || false,
        expenses: stage.expenses ? (typeof stage.expenses === 'string' ? JSON.parse(stage.expenses) : stage.expenses) : []
      })) || [],
      careActivities: data.care_activities || []
    };
  };

  const refreshFarmData = async () => {
    try {
      const result = await authAPI.getSingleFarmManagement(id);

      if (result.status && !result.__fromCache) {
        const data = result.data;
        const formattedProduce = formatProduceData(data);
        setProduce(formattedProduce);
      }
    } catch (error) {
      console.error('Error refreshing farm data:', error);
    }
  };

  const handleUpdateStage = async () => {
    try {
      setIsUpdatingStage(true);

      const selectedStage = produce.stages.find(stage => stage.name === produce.currentStage);
      if (!selectedStage) {
        showToast('error', 'Selected stage not found');
        return;
      }

      const stageData = {
        is_current: true,
        start_date: produce.startDate,
        end_date: produce.expectedHarvest
      };

      const result = await authAPI.updateFarmStage(selectedStage.id, stageData);

      if (result.status) {
        if (result.__offline) {
          setProduce(prev => ({
            ...prev,
            stages: prev.stages.map(s => ({
              ...s,
              current: s.id === selectedStage.id,
              date: s.id === selectedStage.id ? stageData.start_date : s.date,
              endDate: s.id === selectedStage.id ? stageData.end_date : s.endDate
            })),
            currentStage: selectedStage.name,
            startDate: stageData.start_date,
            expectedHarvest: stageData.end_date
          }));
        }

        showToast('success', result.message || 'Stage updated successfully');
        setIsEditing(false);
        if (!result.__offline) await refreshFarmData();
      } else {
        showToast('error', result.message || 'Failed to update stage');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      showToast('error', 'Failed to update stage');
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleAddStage = async (e) => {
    e.preventDefault();

    try {
      setIsAddingStage(true);

      const stageExpenses = expenseForms.map((form) => ({
        amount: parseFloat(form.amount) || 0,
        expense_type: form.type,
        note: form.notes,
      }));

      const stageData = {
        stage_name: newStage.name,
        start_date: newStage.date,
        end_date: newStage.endDate,
        expenses: stageExpenses
      };

      const result = await authAPI.addFarmStage(id, stageData);

      if (result.status) {
        if (result.__offline) {
          const stageCost = stageExpenses.reduce((sum, exp) => sum + exp.amount, 0);
          const optimisticStage = {
            id: result.data ? result.data.id : `temp_${Date.now()}`,
            name: newStage.name,
            date: newStage.date,
            endDate: newStage.endDate,
            cost: stageCost,
            current: false,
            expenses: stageExpenses
          };

          setProduce(prev => ({
            ...prev,
            stages: [...prev.stages, optimisticStage],
            totalExpenses: prev.totalExpenses + stageCost
          }));
        }

        showToast('success', result.message || 'Stage added successfully');
        setNewStage({ name: "", date: "", endDate: "", cost: "" });
        setExpenseForms([{ id: 1, type: "", amount: "", notes: "" }]);
        setShowStageAddForm(false);
        if (!result.__offline) await refreshFarmData();
      } else {
        showToast('error', result.message || 'Failed to add stage');
      }
    } catch (error) {
      console.error('Error adding stage:', error);
      showToast('error', 'Failed to add stage');
    } finally {
      setIsAddingStage(false);
    }
  };

  const handleAddExpenseForm = () => {
    const newId =
      expenseForms.length > 0
        ? Math.max(...expenseForms.map((form) => form.id)) + 1
        : 1;
    setExpenseForms([
      ...expenseForms,
      { id: newId, type: "", amount: "", notes: "" },
    ]);
  };

  const handleRemoveExpenseForm = (id) => {
    if (expenseForms.length > 1) {
      setExpenseForms(expenseForms.filter((form) => form.id !== id));
    }
  };

  const handleExpenseFormChange = (id, field, value) => {
    setExpenseForms(
      expenseForms.map((form) =>
        form.id === id ? { ...form, [field]: value } : form
      )
    );
  };

  const handleAddCare = async (e) => {
    e.preventDefault();

    try {
      setIsAddingCare(true);

      const careData = {
        action: newCare.action,
        date: newCare.date,
        cost: parseFloat(newCare.cost) || 0,
        notes: newCare.notes
      };

      const result = await authAPI.addFarmActivity(id, careData);

      if (result.status) {
        if (result.__offline) {
          const optimisticCare = {
            id: result.data ? result.data.id : `temp_${Date.now()}`,
            ...careData
          };

          setProduce(prev => ({
            ...prev,
            careActivities: [optimisticCare, ...prev.careActivities],
            careExpenses: prev.careExpenses + careData.cost,
            totalExpenses: prev.totalExpenses + careData.cost
          }));
        }

        showToast('success', result.message || 'Activity added successfully');
        setNewCare({ action: "", date: "", cost: "", notes: "" });
        if (!result.__offline) await refreshFarmData();
      } else {
        showToast('error', result.message || 'Failed to add activity');
      }
    } catch (error) {
      console.error('Error adding care activity:', error);
      showToast('error', 'Failed to add care activity');
    } finally {
      setIsAddingCare(false);
    }
  };

  const deleteCareActivity = async (careId) => {
    try {
      setIsDeletingCare(true);
      const result = await authAPI.deleteFarmActivity(careId);

      if (result.status) {
        if (result.__offline) {
          const deletedActivity = produce.careActivities.find(a => a.id === careId);
          if (deletedActivity) {
            setProduce(prev => ({
              ...prev,
              careActivities: prev.careActivities.filter(a => a.id !== careId),
              careExpenses: prev.careExpenses - (deletedActivity.cost || 0),
              totalExpenses: prev.totalExpenses - (deletedActivity.cost || 0)
            }));
          }
        }
        showToast('success', result.message || 'Activity deleted successfully');
        if (!result.__offline) await refreshFarmData();
      }
    } catch (error) {
      console.error('Error deleting care activity:', error);
      showToast('error', 'Failed to delete care activity');
    } finally {
      setIsDeletingCare(false);
    }
  };

  const toggleStageExpansion = (stageId) => {
    setExpandedStages({
      ...expandedStages,
      [stageId]: !expandedStages[stageId],
    });
  };

  const updateFarmStage = async (stageId, stageData) => {
    try {
      const result = await authAPI.updateFarmStage(stageId, stageData);

      if (result.status) {
        showToast('success', result.message || 'Stage updated successfully');
        if (!result.__offline) await refreshFarmData();
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      showToast('error', 'Failed to update stage');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      Planting: "#3498db",
      Germination: "#9b59b6",
      Vegetative: "#2ecc71",
      Flowering: "#e74c3c",
      Fruiting: "#f39c12",
      Harvest: "#0e4410",
      Newborn: "#3498db",
      Juvenile: "#9b59b6",
      Growth: "#2ecc71",
      Mature: "#f39c12",
      Breeding: "#e74c3c",
    };
    return colors[stage] || "#95a5a6";
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!produce) return <div className="loading">No data found</div>;

  return (
    <div className="manage-produce-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
            <button
              className="toast-close-fxk"
              onClick={() => setToast({ show: false, type: '', message: '' })}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <div className="manage-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          <ChevronLeft size={20} /> Back
        </button>
        <h1>Manage {produce.name}</h1>
      </div>

      <div className="produce-tabs">
        <button
          className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === "stages" ? "active" : ""}`}
          onClick={() => setActiveTab("stages")}
        >
          Growth Stages
        </button>
        <button
          className={`tab-btn ${activeTab === "care" ? "active" : ""}`}
          onClick={() => setActiveTab("care")}
        >
          Care Activities
        </button>
      </div>

      {activeTab === "overview" && (
        <div className="overview-tab">
          <div className="overview-card">
            <div className="overview-header">
              <h2>Current Status</h2>
              {!isEditing ? (
                <button onClick={() => setIsEditing(true)} className="edit-btn">
                  <Edit size={16} /> Edit
                </button>
              ) : (
                <div className="edit-actions">
                  <button
                    onClick={handleUpdateStage}
                    className="save-btn"
                    disabled={isUpdatingStage}
                  >
                    {isUpdatingStage ? (
                      <>
                        <div className="loading"></div> Saving...
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="cancel-btn"
                  >
                    <X size={16} /> Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="status-info">
              <div className="status-item">
                <label>Current Stage</label>
                {isEditing ? (
                  <select
                    value={produce.currentStage}
                    onChange={(e) => {
                      const selectedStageName = e.target.value;
                      const selectedStage = produce.stages.find(stage => stage.name === selectedStageName);

                      if (selectedStage) {
                        setProduce({
                          ...produce,
                          currentStage: selectedStageName,
                          startDate: selectedStage.date,
                          expectedHarvest: selectedStage.endDate
                        });
                      }
                    }}
                  >
                    {produce.stages.map((stage) => (
                      <option key={stage.id} value={stage.name}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div
                    className="stage-badge"
                    style={{
                      backgroundColor: getStageColor(produce.currentStage),
                    }}
                  >
                    {produce.currentStage}
                  </div>
                )}
              </div>

              <div className="status-item">
                <label>Next Stage</label>
                <p>{produce.nextStage}</p>
              </div>

              <div className="status-item">
                <label>Start Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={produce.startDate}
                    onChange={(e) =>
                      setProduce({ ...produce, startDate: e.target.value })
                    }
                  />
                ) : (
                  <p>{produce.startDate}</p>
                )}
              </div>

              <div className="status-item">
                <label>
                  Expected {produce.type === "crop" ? "Harvest" : "Maturity"}
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={produce.expectedHarvest}
                    onChange={(e) =>
                      setProduce({
                        ...produce,
                        expectedHarvest: e.target.value,
                      })
                    }
                  />
                ) : (
                  <p>{produce.expectedHarvest}</p>
                )}
              </div>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <DollarSign size={20} />
                <h3>Total Expenses</h3>
              </div>
              <div className="stat-value">
                ₦{produce.totalExpenses.toLocaleString()}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <DollarSign size={20} />
                <h3>Care Expenses</h3>
              </div>
              <div className="stat-value">
                ₦{produce.careExpenses.toLocaleString()}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <Calendar size={20} />
                <h3>Days to Harvest</h3>
              </div>
              <div className="stat-value">32 days</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "stages" && (
        <div className="stages-tab">
          <div className="stages-list">
            <button
              type="button"
              className="add-btn"
              onClick={() => setShowStageAddForm(true)}
              style={{ marginBottom: "15px" }}
            >
              <Plus size={16} /> Add New Stage
            </button>
            {produce.stages.map((stage) => (
              <div key={stage.id}>
                <div
                  className={`stage-item ${stage.current ? "current" : ""}`}
                  onClick={() => toggleStageExpansion(stage.id)}
                >
                  <div className="stage-home">
                    <div
                      className="stage-marker"
                      style={{ backgroundColor: getStageColor(stage.name) }}
                    ></div>
                    <div className="stage-details">
                      <h3>
                        <p>Stage :</p>
                        {stage.name}
                      </h3>
                      <div className="stage-date" style={{ marginBottom: "5px" }}>
                        <p>Stage Start:</p>
                        <Calendar size={14} /> {stage.date}
                      </div>
                      {stage.endDate && (
                        <div className="stage-date">
                          <p>Stage End:</p>
                          <Calendar size={14} /> {stage.endDate}
                        </div>
                      )}
                      {stage.cost > 0 && (
                        <div className="stage-cost">
                          <p>Stage Expense :</p>
                          <DollarSign size={14} /> ₦
                          {stage.cost.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {expandedStages[stage.id] ? <ChevronUp /> : <ChevronDown />}
                  {stage.current && (
                    <div className="current-badge">Current</div>
                  )}
                </div>

                {expandedStages[stage.id] && (
                  <div
                    className="stage-expenses"
                    style={{
                      padding: "15px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "0 0 8px 8px",
                      marginTop: "-1px",
                      marginBottom: "15px",
                      border: "1px solid #eee",
                      borderTop: "none",
                    }}
                  >
                    <h4 style={{ marginTop: 0, marginBottom: "10px" }}>
                      Stage Expenses
                    </h4>
                    {stage.expenses && stage.expenses.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {stage.expenses.map((expense) => (
                          <div
                            key={expense.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "10px",
                              backgroundColor: "white",
                              borderRadius: "6px",
                              border: "1px solid #eee",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: "500" }}>
                                {expense.expense_type}
                              </div>
                              <div style={{ fontSize: "14px", color: "#666" }}>
                                {expense.note}
                              </div>
                            </div>
                            <div
                              style={{ fontWeight: "600", color: "#0e4410" }}
                            >
                              ₦{expense.amount.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No expenses recorded for this stage.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {showStageAddForm && (
            <div className="modal-overlay_ffx">
              <div className="modal-content_ffx">
                <div className="modal-header_ffx">
                  <h3>Add New Stage</h3>
                  <button
                    className="close-button_ffx"
                    onClick={() => {
                      setShowStageAddForm(false);
                      setExpenseForms([
                        { id: 1, type: "", amount: "", notes: "" },
                      ]);
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                <form onSubmit={handleAddStage} className="add-stage-form">
                  <div className="form-group">
                    <label>Stage Name</label>
                    <select
                      value={newStage.name}
                      onChange={(e) =>
                        setNewStage({ ...newStage, name: e.target.value })
                      }
                      required
                    >
                      <option value="">Select a stage</option>
                      <option value="Planting">Planting</option>
                      <option value="Germination">Germination</option>
                      <option value="Vegetative">Vegetative</option>
                      <option value="Flowering">Flowering</option>
                      <option value="Fruiting">Fruiting</option>
                      <option value="Harvest">Harvest</option>
                      {produce.type !== "crop" && (
                        <>
                          <option value="Newborn">Newborn</option>
                          <option value="Juvenile">Juvenile</option>
                          <option value="Growth">Growth</option>
                          <option value="Mature">Mature</option>
                          <option value="Breeding">Breeding</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={newStage.date}
                        onChange={(e) =>
                          setNewStage({ ...newStage, date: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={newStage.endDate}
                        onChange={(e) =>
                          setNewStage({ ...newStage, endDate: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="expense-header" style={{ marginTop: "15px" }}>
                    <h3>Expenses</h3>
                    <button
                      type="button"
                      className="add-btn"
                      onClick={handleAddExpenseForm}
                    >
                      <Plus size={16} /> Add Expense
                    </button>
                  </div>

                  {expenseForms.map((form, index) => (
                    <div
                      key={form.id}
                      className="expense-form"
                      style={{
                        padding: "15px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "8px",
                        marginBottom: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "10px",
                        }}
                      >
                        <h4 style={{ margin: 0 }}>Expense #{index + 1}</h4>
                        {expenseForms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveExpenseForm(form.id)}
                            className="delete-btn"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Amount (₦)</label>
                          <input
                            type="number"
                            value={form.amount}
                            onChange={(e) =>
                              handleExpenseFormChange(
                                form.id,
                                "amount",
                                e.target.value
                              )
                            }
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Expense Type</label>
                          <input
                            type="text"
                            value={form.type}
                            onChange={(e) =>
                              handleExpenseFormChange(
                                form.id,
                                "type",
                                e.target.value
                              )
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          value={form.notes}
                          onChange={(e) =>
                            handleExpenseFormChange(
                              form.id,
                              "notes",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}

                  <div className="button-row">
                    <button
                      type="submit"
                      className="add-btn"
                      disabled={isAddingStage}
                    >
                      {isAddingStage ? (
                        <>
                          <div className="loading"></div> Saving...
                        </>
                      ) : (
                        <>
                          <Check size={16} /> Save
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowStageAddForm(false);
                        setExpenseForms([
                          { id: 1, type: "", amount: "", notes: "" },
                        ]);
                      }}
                    >
                      <X size={16} /> Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "care" && (
        <div className="care-tab">
          <div className="care-list">
            {produce.careActivities.map((activity) => (
              <div key={activity.id} className="care-item">
                <div className="care-details">
                  <h3>{activity.action}</h3>
                  <div className="care-date">
                    <Calendar size={14} /> {activity.date}
                  </div>
                  <p>{activity.notes}</p>
                </div>
                <div className="care-actions">
                  {activity.cost > 0 && (
                    <div className="care-cost">
                      ₦{activity.cost.toLocaleString()}
                    </div>
                  )}
                  <button
                    onClick={() => deleteCareActivity(activity.id)}
                    className="delete-btn"
                    disabled={isDeletingCare}
                  >
                    {isDeletingCare ? (
                      <div className="loading"></div>
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleAddCare} className="add-care-form">
            <h3>Add Care Activity</h3>
            <div className="form-group">
              <label>Action</label>
              <input
                type="text"
                value={newCare.action}
                onChange={(e) =>
                  setNewCare({ ...newCare, action: e.target.value })
                }
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={newCare.date}
                  onChange={(e) =>
                    setNewCare({ ...newCare, date: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Cost (₦)</label>
                <input
                  type="number"
                  value={newCare.cost}
                  onChange={(e) =>
                    setNewCare({ ...newCare, cost: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={newCare.notes}
                onChange={(e) =>
                  setNewCare({ ...newCare, notes: e.target.value })
                }
              />
            </div>
            <button
              type="submit"
              className="add-btn"
              disabled={isAddingCare}
            >
              {isAddingCare ? (
                <>
                  <div className="loading"></div> Adding...
                </>
              ) : (
                <>
                  <Plus size={16} /> Add Activity
                </>
              )}
            </button>
          </form>
        </div>
      )}

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        .toast-close-fxk {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close-fxk:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
